import type {
  CsvAnalyzeResponse,
  CsvParseResponse,
  LicenceResponse,
  ListResponse,
  ProfileResponse,
} from '@shared/schemas';
import {
  all,
  call,
  debounce,
  getContext,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';

import type { FilterGroup } from '@/types/filters';
import { classifyInfraError } from '@/utils/infra-error';

import { buildEnrichmentStats, EMPTY_ENRICHMENT_STATS } from '../../_shared/domain/enrichment';
import type { FileUploadState } from '../../_shared/domain/file-import.model';
import { toListWizardError } from '../../_shared/domain/list-error';
import { buildSavePlan } from '../../_shared/domain/save-plan';
import type { EditDraft, ProfileSelection } from '../domain/edit-draft.model';
import { validateDraft } from '../domain/edit-draft.validation';
import type {
  BucketsRaw,
  EditListGateway,
  ImportDuplicates,
  SourceListOrigin,
} from '../infrastructure/edit-list.gateway';
import {
  bucketsRequested,
  deleteRequested,
  fileAnalyzeRequested,
  fileParseRequested,
  importPreviewRequested,
  licencesRequested,
  listImportRequested,
  loadRequested,
  scopeAnalyzeRequested,
  sourceListsRequested,
  submitCancelled,
  submitRequested,
} from './edit-list.actions';
import {
  selectDraft,
  selectExcludeDuplicates,
  selectFileImportData,
  selectFileUpload,
  selectImportedProfileIds,
  selectImportPreviewPage,
  selectImportPreviewPageSize,
  selectListId,
  selectLoadedLicenceId,
  selectProfilePage,
  selectProfilePageSize,
  selectProfileTotal,
  selectServerFilters,
  selectSourceLicenceId,
  selectSourceOrigin,
} from './edit-list.selectors';
import { editListActions } from './edit-list.slice';

/** WORKER: load the list to edit and hydrate the draft. */
function* loadWorker(action: ReturnType<typeof loadRequested>) {
  const gateway: EditListGateway = yield getContext('editListGateway');
  yield put(editListActions.loadStarted());
  try {
    const list = (yield call(gateway.loadList, action.payload)) as ListResponse;
    yield put(
      editListActions.loadSucceeded({
        listId: list.id,
        licenceId: list.licence_id,
        licenceName: list.licence_name ?? '',
        name: list.name,
        startDate: list.start_date.slice(0, 10),
        endDate: list.end_date.slice(0, 10),
        // Admins only reach Edit list, and admins are served the scope (#718).
        scope: list.scope ?? '',
        clientScopeDescription: list.client_scope_description,
        origin: list.origin,
        status: list.status,
      }),
    );
  } catch {
    yield put(editListActions.loadFailed());
  }
}

/**
 * Core save orchestration: update → (import) → (associate) → (enrich). Each
 * optional step is decided by the pure `buildSavePlan`. All I/O goes through the
 * injected gateway; errors are mapped to domain errors and surfaced via
 * `saveFailed`. Dispatches `saveSucceeded` on completion.
 */
function* runSave(selection: ProfileSelection, draft: EditDraft) {
  const gateway: EditListGateway = yield getContext('editListGateway');
  const plan = buildSavePlan(draft, selection);
  const loadedLicenceId = (yield select(selectLoadedLicenceId)) as string;
  try {
    yield put(editListActions.savePhaseSet('Updating list…'));
    yield call(gateway.updateList, draft, { transferLicence: draft.licenceId !== loadedLicenceId });

    if (plan.fileImport) {
      yield put(editListActions.savePhaseSet('Importing profiles from file…'));
      yield call(gateway.importFile, draft.listId, plan.fileImport);
    }
    if (plan.finalIds.length > 0) {
      yield put(editListActions.savePhaseSet('Associating profiles…'));
      yield call(gateway.associateProfiles, draft.listId, plan.finalIds);
    }
    if (plan.enrichTargetIds.length > 0) {
      yield put(editListActions.savePhaseSet('Queueing enrichment session…'));
      yield call(gateway.queueEnrichment, draft.listId, plan.enrichTargetIds);
    }
    yield put(editListActions.saveSucceeded());
  } catch (e) {
    yield put(editListActions.saveFailed(toListWizardError(e)));
  }
}

/** Validate the draft up front; returns true when the flow may proceed. */
function* validateOrFail(draft: EditDraft) {
  const errors = validateDraft(draft);
  if (Object.keys(errors).length > 0) {
    yield put(editListActions.saveFailed({ kind: 'validation', fields: errors }));
    return false;
  }
  return true;
}

/**
 * WORKER: full save. Validates, then races the save against an explicit
 * `submitCancelled`. If cancellation wins, the save branch is auto-cancelled and
 * we flag it. (A fresh `submitRequested` also cancels this worker via
 * `takeLatest`; that supersession is silent: the new run governs the state.)
 */
function* saveWorker(action: ReturnType<typeof submitRequested>) {
  const draft = (yield select(selectDraft)) as EditDraft;
  if (!((yield call(validateOrFail, draft)) as boolean)) return;

  yield put(editListActions.saveStarted());
  const outcome: { cancelled?: unknown } = yield race({
    done: call(runSave, action.payload, draft),
    cancelled: take(submitCancelled.type),
  });
  if (outcome.cancelled !== undefined) {
    yield put(editListActions.saveFailed({ kind: 'cancelled' }));
  }
}

/** WORKER: delete the list currently being edited. */
function* deleteWorker() {
  const gateway: EditListGateway = yield getContext('editListGateway');
  const listId = (yield select(selectListId)) as string;
  if (!listId) return;

  yield put(editListActions.deleteStarted());
  try {
    yield call(gateway.deleteList, listId);
    yield put(editListActions.deleteSucceeded());
  } catch (e) {
    yield put(editListActions.deleteFailed(toListWizardError(e)));
  }
}

// ── Profile-selection data layer ──
//
// The debounce that used to live inside `useAdvancedFilters` (500ms) now lives
// here: a filter change waits, then resets to page 0, which is the single
// trigger for a fetch. Direct pagination fetches immediately.

/** All actions that change the pruned filter set (⇒ page reset + refetch). */
const FILTER_CHANGE_TYPES = [
  editListActions.ruleAdded.type,
  editListActions.ruleUpdated.type,
  editListActions.nodeRemoved.type,
  editListActions.groupAdded.type,
  editListActions.combinatorSet.type,
  editListActions.nodeMoved.type,
  editListActions.filtersCleared.type,
  editListActions.filtersLoaded.type,
  editListActions.excludeDuplicatesToggled.type,
];

/**
 * WORKER: a filter changed: reset to the first page. `pageChanged` is the sole
 * fetch trigger, so this dispatch (even to the same page 0) drives exactly one
 * refetch through `fetchProfilesWorker`.
 */
function* resetToFirstPageWorker() {
  yield put(editListActions.pageChanged(0));
}

/** WORKER: fetch one page of profiles for the current filters/page. Cancellable. */
function* fetchProfilesWorker() {
  const gateway: EditListGateway = yield getContext('editListGateway');
  const filters = (yield select(selectServerFilters)) as FilterGroup | null;
  const page = (yield select(selectProfilePage)) as number;
  const pageSize = (yield select(selectProfilePageSize)) as number;
  const excludeDuplicates = (yield select(selectExcludeDuplicates)) as boolean;
  const draft = (yield select(selectDraft)) as EditDraft;

  yield put(editListActions.profilesLoading());
  try {
    const { rows, total } = (yield call(gateway.fetchProfiles, {
      filters,
      page,
      pageSize,
      excludeDuplicates,
      licenceId: draft.licenceId || undefined,
    })) as { rows: ProfileResponse[]; total: number };
    yield put(editListActions.profilesLoaded({ rows, total }));
  } catch {
    // Non-blocking: clear the spinner, keep the previous rows.
    yield put(editListActions.profilesLoaded({ rows: [], total: 0 }));
  }
}

/** WORKER: recompute the live duplicate count (raw total minus filtered total). */
function* duplicateWorker() {
  const excludeDuplicates = (yield select(selectExcludeDuplicates)) as boolean;
  if (!excludeDuplicates) {
    yield put(editListActions.duplicateCountSet(0));
    return;
  }
  const gateway: EditListGateway = yield getContext('editListGateway');
  const filters = (yield select(selectServerFilters)) as FilterGroup | null;

  yield put(editListActions.duplicateLoadingStarted());
  try {
    const rawTotal = (yield call(gateway.countRawTotal, filters)) as number;
    const filteredTotal = (yield select(selectProfileTotal)) as number;
    yield put(editListActions.duplicateCountSet(Math.max(0, rawTotal - filteredTotal)));
  } catch {
    yield put(editListActions.duplicateCountSet(0));
  }
}

/** WORKER: fetch + segment the enrichment buckets for the confirm step. */
function* bucketsWorker() {
  const gateway: EditListGateway = yield getContext('editListGateway');
  yield put(editListActions.bucketsLoading());

  const filters = (yield select(selectServerFilters)) as FilterGroup | null;
  const excludeDuplicates = (yield select(selectExcludeDuplicates)) as boolean;
  const importedProfileIds = (yield select(selectImportedProfileIds)) as string[];
  const fileImportData = (yield select(selectFileImportData)) as {
    analyzeResult?: { existing_profile_ids?: string[] };
  } | null;
  const draft = (yield select(selectDraft)) as EditDraft;

  const fileExistingIds = fileImportData?.analyzeResult?.existing_profile_ids ?? [];
  const importedIds = [...importedProfileIds, ...fileExistingIds];

  try {
    const raw = (yield call(gateway.fetchForBuckets, {
      filters,
      excludeDuplicates,
      importedIds,
      licenceId: draft.licenceId || undefined,
      includeFilterResults: draft.methods.includes('select'),
    })) as BucketsRaw;
    const stats = buildEnrichmentStats(
      raw.filterProfiles,
      raw.rawTotal,
      raw.importedProfiles,
      excludeDuplicates,
    );
    yield put(editListActions.bucketsLoaded(stats));
  } catch {
    yield put(editListActions.bucketsLoaded(EMPTY_ENRICHMENT_STATS));
  }
}

/** WORKER: analyse the scope text into a filter tree, then load it. */
function* nlSearchWorker() {
  const draft = (yield select(selectDraft)) as EditDraft;
  const trimmed = draft.scope.trim();
  if (!trimmed) return;

  const gateway: EditListGateway = yield getContext('editListGateway');
  yield put(editListActions.nlSearchStarted());
  try {
    const group = (yield call(gateway.analyzeScope, trimmed)) as FilterGroup;
    yield put(editListActions.filtersLoaded(group));
  } catch {
    // Non-blocking; the user keeps their current filters.
  } finally {
    yield put(editListActions.nlSearchEnded());
  }
}

/** WORKER: parse the file dropped on the embedded import step. */
function* fileParseWorker(action: ReturnType<typeof fileParseRequested>) {
  const gateway: EditListGateway = yield getContext('editListGateway');
  yield put(editListActions.fileParseStarted({ fileName: action.payload.name }));
  try {
    const result = (yield call(gateway.parseFile, action.payload)) as CsvParseResponse;
    // A file the backend could not read at all: surface the first error and
    // stay on the Upload sub-step (parity with the original stepper).
    if (result.errors.length > 0 && result.row_count === 0) {
      yield put(
        editListActions.fileParseFailed({ kind: 'api', message: result.errors[0], status: 0 }),
      );
      return;
    }
    yield put(editListActions.fileParseSucceeded(result));
  } catch (e) {
    yield put(editListActions.fileParseFailed(classifyInfraError(e, 'Failed to parse file')));
  }
}

/** WORKER: dry-run the mapped rows of the embedded import step. */
function* fileAnalyzeWorker() {
  const fileUpload = (yield select(selectFileUpload)) as FileUploadState;
  if (!fileUpload.parseResult) return; // the UI can't request this before a parse

  const gateway: EditListGateway = yield getContext('editListGateway');
  yield put(editListActions.fileAnalyzeStarted());
  try {
    const result = (yield call(gateway.analyzeRows, {
      column_mapping: fileUpload.columnMapping,
      rows: fileUpload.parseResult.rows,
    })) as CsvAnalyzeResponse;
    yield put(editListActions.fileAnalyzeSucceeded(result));
  } catch (e) {
    yield put(editListActions.fileAnalyzeFailed(classifyInfraError(e, 'Analysis failed')));
  }
}

/**
 * WORKER: load the licence dropdown of the import step. A failure lands an
 * empty dropdown silently, matching the old `useApi` behaviour.
 */
function* licencesWorker() {
  const gateway: EditListGateway = yield getContext('editListGateway');
  yield put(editListActions.licencesLoadStarted());
  try {
    const rows = (yield call(gateway.fetchLicences)) as LicenceResponse[];
    yield put(editListActions.licencesLoaded(rows));
  } catch {
    yield put(editListActions.licencesLoaded([]));
  }
}

/**
 * WORKER: load the source-list dropdown. Reads origin + licence from the slice
 * (they are the request's parameters). `takeLatest` on the three triggering
 * actions replaces the old `useApi` abort-on-path-change: a superseded run is
 * cancelled so a stale response can never overwrite a newer one. A failure lands
 * an empty dropdown, silently.
 */
function* sourceListsWorker() {
  const gateway: EditListGateway = yield getContext('editListGateway');
  const origin = (yield select(selectSourceOrigin)) as SourceListOrigin;
  const licenceId = (yield select(selectSourceLicenceId)) as string;

  yield put(editListActions.sourceListsLoadStarted());
  try {
    const rows = (yield call(gateway.fetchSourceLists, {
      origin,
      ...(licenceId ? { licenceId } : {}),
    })) as ListResponse[];
    yield put(editListActions.sourceListsLoaded(rows));
  } catch {
    yield put(editListActions.sourceListsLoaded([]));
  }
}

/**
 * WORKER: import every profile of the chosen source list.
 *
 * The duplicate check is best-effort: if it fails, the import still goes through
 * with ALL ids and no duplicates reported, exactly as the old step did. It is
 * skipped entirely when there is no target licence or nothing to check. A
 * failure of the id fetch itself is silent; the button just re-enables.
 */
function* listImportWorker(action: ReturnType<typeof listImportRequested>) {
  const { listId, listName } = action.payload;
  const gateway: EditListGateway = yield getContext('editListGateway');
  const draft = (yield select(selectDraft)) as { licenceId: string };

  yield put(editListActions.listImportStarted());
  try {
    const rawIds = (yield call(gateway.fetchListProfileIds, listId)) as string[];

    let duplicateIds: string[] = [];
    if (draft.licenceId && rawIds.length > 0) {
      try {
        const dup = (yield call(
          gateway.checkImportDuplicates,
          rawIds,
          draft.licenceId,
        )) as ImportDuplicates;
        duplicateIds = dup.duplicateIds;
      } catch {
        duplicateIds = [];
      }
    }

    yield put(editListActions.listImportSucceeded({ rawIds, duplicateIds, listName }));
  } catch {
    yield put(editListActions.listImportFailed());
  }
}

/**
 * WORKER: fetch one page of the imported-profiles preview. Ids/page/pageSize
 * are read from the slice; an empty selection settles to an empty table without
 * a request. Silent on failure (clears the spinner), matching the old hook.
 */
function* importPreviewWorker() {
  const ids = (yield select(selectImportedProfileIds)) as string[];
  if (ids.length === 0) {
    yield put(editListActions.importPreviewLoaded({ rows: [], total: 0 }));
    return;
  }
  const gateway: EditListGateway = yield getContext('editListGateway');
  const page = (yield select(selectImportPreviewPage)) as number;
  const pageSize = (yield select(selectImportPreviewPageSize)) as number;

  yield put(editListActions.importPreviewLoading());
  try {
    const { rows, total } = (yield call(gateway.fetchProfilesByIds, ids, page, pageSize)) as {
      rows: ProfileResponse[];
      total: number;
    };
    yield put(editListActions.importPreviewLoaded({ rows, total }));
  } catch {
    yield put(editListActions.importPreviewLoaded({ rows: [], total: 0 }));
  }
}

/** WATCHERS. */
export function* editListSaga() {
  yield all([
    takeLatest(loadRequested.type, loadWorker),
    takeLatest(submitRequested.type, saveWorker),
    takeLatest(deleteRequested.type, deleteWorker),
    // Profile table: filter change → 500ms → reset to page 0 → fetch.
    debounce(500, FILTER_CHANGE_TYPES, resetToFirstPageWorker),
    takeLatest(
      [editListActions.pageChanged.type, editListActions.pageSizeChanged.type],
      fetchProfilesWorker,
    ),
    // Live duplicate count: 400ms debounce on the same filter changes.
    debounce(400, FILTER_CHANGE_TYPES, duplicateWorker),
    // Enrichment buckets + NL scope search (explicit signals from the UI).
    takeLatest(bucketsRequested.type, bucketsWorker),
    takeLatest(scopeAnalyzeRequested.type, nlSearchWorker),
    // Embedded file-import flow.
    takeLatest(fileParseRequested.type, fileParseWorker),
    takeLatest(fileAnalyzeRequested.type, fileAnalyzeWorker),
    // Import-from-list step: dropdown reload is the abort-on-change replacement.
    takeLatest(licencesRequested.type, licencesWorker),
    takeLatest(
      [
        sourceListsRequested.type,
        editListActions.sourceOriginChanged.type,
        editListActions.sourceLicenceChanged.type,
      ],
      sourceListsWorker,
    ),
    takeLatest(listImportRequested.type, listImportWorker),
    // Imported-profiles preview: refetch on import / exclusion toggle / pagination.
    takeLatest(importPreviewRequested.type, importPreviewWorker),
  ]);
}
